import { Box, Button, Grid, IconButton, Tooltip } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import { FreeTrialDialog } from "components/shared/free-trial-dialog";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import { postCustomCTA } from "adapters";
import { CustomCTA as CUSTOMCTA, GetUrlBuilderData, SetUrlBuilderData } from "store/url-builder";
import dashedButtonStyle from "styles/buttons/dashed-button.module.scss";
import roundedButtonStyle from "styles/buttons/rounded-button.module.scss";
import { isWhite } from "utils";
import { CustomCTADialog } from "./custom-cta-dialog";

// Flag to control draggable sections visibility
const SHOW_DRAGGABLE_SECTIONS = false;

export interface CustomCTAProps {
  builderType: string;
  isFree?: boolean | null;
  duplicateId?: string;
  initialCTA?: { buttonText: string; buttonLink: string; buttonColor?: string };
}

export const CustomCTA = ({ builderType, isFree = null, duplicateId, initialCTA }: CustomCTAProps) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [freeTrialDialog, setFreeTrialDialog] = useState(false);
  const builderData = useSelector(GetUrlBuilderData);
  const dispatch = useDispatch();
  const { cardId }: { cardId: string } = useParams();

  const [ctaList, setCtaList] = useState<CUSTOMCTA[]>([]);

  useEffect(() => {
    // Use duplicate section CTA if provided, otherwise use the main customCTA
    const ctaData = duplicateId && initialCTA ? initialCTA : builderData.customCTA;
    
    const buttonTexts = ctaData.buttonText
      ? ctaData.buttonText.split("#lcard;")
      : [];
    const buttonLinks = ctaData.buttonLink
      ? ctaData.buttonLink.split("#lcard;")
      : [];
    const buttonColor = ctaData.buttonColor
      ? ctaData.buttonColor.split("#lcard;")
      : buttonTexts.map((item) => builderData.primaryColor.color);

    let temp: CUSTOMCTA[] = [];

    buttonTexts.forEach((item, index) => {
      if (item && buttonLinks[index])
        temp.push({
          buttonText: item,
          buttonLink: buttonLinks[index],
          buttonColor: buttonColor[index],
        });
    });

    setCtaList(temp);
  }, [builderData.customCTA, builderData.primaryColor.color, duplicateId, initialCTA]);

  const openFreeTrialDialog = () => setFreeTrialDialog(true);
  const handleCloseFreeTrialDialog = () => setFreeTrialDialog(false);

  // Reorder function for drag and drop
  const reorder = <T,>(
    list: T[],
    startIndex: number,
    endIndex: number
  ): T[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  // Helper function to update CTA order via API
  const updateCTAOrder = (newList: CUSTOMCTA[]) => {
    // Reconstruct the delimited strings
    const reorderedCTA: { buttonText: string; buttonLink: string; buttonColor: string; _duplicateId?: string } = {
      buttonText: "",
      buttonLink: "",
      buttonColor: "",
    };

    newList.forEach((button) => {
      reorderedCTA.buttonText = reorderedCTA.buttonText
        ? reorderedCTA.buttonText + button.buttonText + "#lcard;"
        : button.buttonText + "#lcard;";
      reorderedCTA.buttonLink = reorderedCTA.buttonLink
        ? reorderedCTA.buttonLink + button.buttonLink + "#lcard;"
        : button.buttonLink + "#lcard;";
      reorderedCTA.buttonColor = reorderedCTA.buttonColor
        ? reorderedCTA.buttonColor + button.buttonColor + "#lcard;"
        : button.buttonColor + "#lcard;";
    });

    // Include duplicateId if it's a duplicate section
    if (duplicateId) {
      reorderedCTA._duplicateId = duplicateId;
    }

    // Update via API
    return postCustomCTA(cardId, builderType, reorderedCTA)
      .then(({ data }) => {
        if (data.success) {
          dispatch(SetUrlBuilderData(data.data));
        }
      })
      .catch((err: any) => {
        console.error('Error updating CTA order:', err);
        throw err;
      });
  };

  // Handle drag end for CTA buttons
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const reorderedList = reorder(
      ctaList,
      result.source.index,
      result.destination.index
    );

    // Check if order actually changed
    const isSame = ctaList.every(
      (item, index) =>
        item.buttonText === reorderedList[index]?.buttonText &&
        item.buttonLink === reorderedList[index]?.buttonLink
    );

    if (!isSame) {
      setCtaList(reorderedList);
      updateCTAOrder(reorderedList).catch(() => {
        // Revert on error
        setCtaList(ctaList);
      });
    }
  };

  // Shuffle function: rotates all buttons down by one (first becomes last)
  const handleShuffle = () => {
    if (ctaList.length < 2) return;

    // Rotate down: move first element to last position
    const shuffledList = [...ctaList];
    const firstItem = shuffledList.shift();
    if (firstItem) {
      shuffledList.push(firstItem);
    }

    setCtaList(shuffledList);
    updateCTAOrder(shuffledList).catch(() => {
      // Revert on error
      setCtaList(ctaList);
    });
  };

  return (
    <Grid item container xs={12}>
      <Grid item xs={12} className="mb-4">
        <Box style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Button
            onClick={!isFree ? () => setOpenDialog(true) : openFreeTrialDialog}
            classes={{ root: dashedButtonStyle.root }}
            style={{ flex: 1 }}
          >
            <AddIcon /> {ctaList.length > 0 ? 'Add/Edit CTA Button' : 'Add Custom CTA Button'}
          </Button>
          {ctaList.length >= 2 && (
            <Button
              onClick={handleShuffle}
              color="primary"
              variant="outlined"
              size="small"
              startIcon={<ArrowDownwardIcon />}
              style={{ textTransform: 'none', padding: '10px 8px' }}
            >
              Rotate Order
            </Button>
          )}
        </Box>
      </Grid>
      {SHOW_DRAGGABLE_SECTIONS ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="cta-buttons-droppable">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} style={{ width: '100%' }}>
                {ctaList.map((cta, index) => (
                  <Draggable
                    key={`cta-${index}`}
                    draggableId={`cta-${index}`}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <Grid
                        item
                        xs={12}
                        className="mb-2"
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          ...provided.draggableProps.style,
                          opacity: snapshot.isDragging ? 0.8 : 1,
                        }}
                      >
                        <Box
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            position: 'relative',
                            width: '100%',
                          }}
                        >
                          <Button
                            onClick={() => window.open(cta.buttonLink, "_blank")}
                            style={{
                              backgroundColor: cta.buttonColor,
                              color: isWhite(cta.buttonColor || "") ? "#262626" : "#fff",
                              border: isWhite(cta.buttonColor || "")
                                ? "1px solid rgba(38,38,38,0.5)"
                                : "none",
                              flex: 1,
                              paddingRight: '48px', // Add padding to make room for drag handle
                            }}
                            className={roundedButtonStyle.root}
                            size="large"
                            variant="contained"
                            color="primary"
                            fullWidth
                          >
                            {cta.buttonText}
                          </Button>
                          <Box
                            {...provided.dragHandleProps}
                            style={{
                              position: 'absolute',
                              right: '8px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              cursor: 'grab',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 10,
                              minWidth: '56px',
                              minHeight: '56px',
                              padding: '12px',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onMouseDown={(e) => {
                              // Stop propagation to prevent section drag, but allow button drag to work
                              // Don't prevent default - react-beautiful-dnd needs it
                              e.stopPropagation();
                            }}
                            onTouchStart={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <IconButton
                              size="small"
                              style={{
                                padding: '4px',
                                color: isWhite(cta.buttonColor || "") ? "#262626" : "#fff",
                              }}
                            >
                              <DragHandleIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <div style={{ width: '100%' }}>
          {ctaList.map((cta, index) => (
            <Grid item xs={12} className="mb-2" key={`cta-${index}`}>
              <Button
                onClick={() => window.open(cta.buttonLink, "_blank")}
                style={{
                  backgroundColor: cta.buttonColor,
                  color: isWhite(cta.buttonColor || "") ? "#262626" : "#fff",
                  border: isWhite(cta.buttonColor || "")
                    ? "1px solid rgba(38,38,38,0.5)"
                    : "none",
                }}
                className={roundedButtonStyle.root}
                size="large"
                variant="contained"
                color="primary"
                fullWidth
              >
                {cta.buttonText}
              </Button>
            </Grid>
          ))}
        </div>
      )}
      {openDialog && (
        <CustomCTADialog
          isOpen={openDialog}
          onClose={() => setOpenDialog(false)}
          builderType={builderType}
          ctaList={ctaList}
          duplicateId={duplicateId}
        />
      )}
      {freeTrialDialog && <FreeTrialDialog isOpen={freeTrialDialog} onClose={handleCloseFreeTrialDialog} />}
    </Grid>
  );
};
